import { AnimatePresence, motion } from "motion/react";
import { useRef, useState } from "react";

const AVATAR_OVERLAP = 10;

const DEFAULT_MEMBERS = [
  { id: "member-1", avatarUrl: "/avatars/face-1.svg", name: "Team Member 1" },
  { id: "member-2", avatarUrl: "/avatars/face-2.svg", name: "Team Member 2" },
  { id: "member-3", avatarUrl: "/avatars/face-3.svg", name: "Team Member 3" },
  { id: "member-4", avatarUrl: "/avatars/face-4.svg", name: "Team Member 4" },
];

const animations = {
  container: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.3, ease: "easeOut" } },
  },
  avatar: {
    visible: { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 300, damping: 24, mass: 0.8 } },
    hidden: { opacity: 0, scale: 0.6, transition: { duration: 0.22, ease: "easeOut" } },
  },
  vibration: {
    idle: { x: 0 },
    shake: { x: [-4, 4, -4, 4, 0], transition: { duration: 0.38, ease: "easeOut" } },
  },
};

export default function TeamSelector({
  members = DEFAULT_MEMBERS,
  defaultValue = 1,
  onChange,
  label = "Team Size",
  className = "",
}) {
  const maxTeamSize = members.length;
  const [peopleCount, setPeopleCount] = useState(defaultValue);
  const [isVibrating, setIsVibrating] = useState(false);
  const directionRef = useRef(1);

  const handleIncrement = (e) => {
    e.preventDefault();
    if (peopleCount < maxTeamSize) {
      directionRef.current = 1;
      const newCount = peopleCount + 1;
      setPeopleCount(newCount);
      onChange?.(newCount);
    } else triggerVibration();
  };

  const handleDecrement = (e) => {
    e.preventDefault();
    if (peopleCount > 1) {
      directionRef.current = -1;
      const newCount = peopleCount - 1;
      setPeopleCount(newCount);
      onChange?.(newCount);
    } else triggerVibration();
  };

  const triggerVibration = () => {
    setIsVibrating(true);
    setTimeout(() => setIsVibrating(false), 380);
  };

  const handleKeyDown = (e, action) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action === "increment" ? handleIncrement(e) : handleDecrement(e);
    }
  };

  return (
    <motion.div
      animate="animate"
      className={`flex w-full h-full flex-col items-center justify-center p-6 gap-5 ${className}`}
      exit="exit"
      initial="initial"
      variants={animations.container}
    >
      <p className="text-xs text-white/40 uppercase tracking-widest font-body font-medium">{label}</p>

      <div className="flex justify-center">
        <motion.div className="flex items-center" layout>
          {members.map((member, index) => (
            <motion.div
              animate={index < peopleCount ? "visible" : "hidden"}
              className="flex items-center justify-center"
              initial={index < defaultValue ? "visible" : "hidden"}
              key={member.id}
              style={{ marginLeft: index === 0 ? 0 : -AVATAR_OVERLAP, zIndex: maxTeamSize - index }}
              variants={animations.avatar}
            >
              <img
                alt={member.name}
                src={member.avatarUrl}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  border: "2px solid rgba(255,255,255,0.15)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                  display: "block",
                  objectFit: "cover",
                  background: "#fff",
                }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>

      <motion.div
        animate={isVibrating ? "shake" : "idle"}
        className="flex items-center justify-center gap-5"
        initial="idle"
        variants={animations.vibration}
      >
        <button
          aria-label="Decrease team size"
          className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/50 transition-all duration-200 hover:border-white/20 hover:text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
          disabled={peopleCount <= 1}
          onClick={handleDecrement}
          onKeyDown={(e) => handleKeyDown(e, "decrement")}
          type="button"
        >
          <span className="select-none font-medium leading-none text-lg">−</span>
        </button>

        <div className="flex min-w-14 flex-col items-center">
          <div className="relative h-9 overflow-hidden">
            <AnimatePresence initial={false} mode="popLayout">
              <motion.output
                animate={{ opacity: 1, y: 0 }}
                aria-label={`Current team size: ${peopleCount}`}
                className="block select-none text-white font-heading font-bold text-2xl"
                exit={{ opacity: 0, y: directionRef.current * -14, transition: { duration: 0.16, ease: "easeIn" } }}
                initial={{ opacity: 0, y: directionRef.current * 14 }}
                key={peopleCount}
                transition={{ duration: 0.22, ease: "easeOut" }}
              >
                {peopleCount}
              </motion.output>
            </AnimatePresence>
          </div>
          <span className="text-xs text-white/35 font-body">
            {peopleCount === 1 ? "member" : "members"}
          </span>
        </div>

        <button
          aria-label="Increase team size"
          className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white/50 transition-all duration-200 hover:border-white/20 hover:text-white hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30"
          disabled={peopleCount >= maxTeamSize}
          onClick={handleIncrement}
          onKeyDown={(e) => handleKeyDown(e, "increment")}
          type="button"
        >
          <span className="select-none font-medium leading-none text-lg">+</span>
        </button>
      </motion.div>

      <p className="text-white/25 text-xs font-body text-center leading-relaxed px-2">
        We adapt to any team size, from solo founders to large squads.
      </p>
    </motion.div>
  );
}
